import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Activity,
  KeyRound,
  RefreshCw,
  MessageSquarePlus,
  Copy,
  Check,
} from 'lucide-react';
import { format } from 'date-fns';
import { activation } from '../api';
import type {
  ActivationState,
  EnforcementLevel,
  Instance,
  NoticeAudience,
  NoticeSeverity,
} from '../types';

/**
 * Activation panel for a self-hosted instance.
 *
 * Rendered only for hostingMode === 'SELF_HOSTED' — for instances on our own
 * servers none of this applies, we just stop the container.
 */

const STATE_LABEL: Record<ActivationState, string> = {
  ACTIVE: 'Aktiv',
  PAYMENT_OVERDUE: 'Zahlung offen',
  SUSPENDED: 'Gesperrt',
  TERMINATED: 'Vertrag beendet',
};

const ENFORCEMENT_LABEL: Record<EnforcementLevel, string> = {
  NONE: 'Keine Einschränkung',
  NOTICE: 'Nur Hinweis',
  READ_ONLY: 'Nur Lesen',
  ADMIN_ONLY: 'Nur Admin + Export',
};

function stateStyle(state: ActivationState): { cls: string; Icon: typeof ShieldCheck } {
  switch (state) {
    case 'ACTIVE':
      return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: ShieldCheck };
    case 'PAYMENT_OVERDUE':
      return { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: ShieldAlert };
    default:
      return { cls: 'bg-red-50 text-red-700 border-red-200', Icon: ShieldX };
  }
}

/**
 * Colours the heartbeat age. The thresholds follow the daily cadence: under 36
 * hours is a normal day plus slack, three days still leaves four days of token
 * validity, beyond that the instance is close to walking down the ladder on its
 * own — which is a support case, not a licensing one.
 */
function heartbeatTone(hours: number | null): string {
  if (hours === null) return 'text-slate-400';
  if (hours <= 36) return 'text-emerald-600';
  if (hours <= 72) return 'text-amber-600';
  return 'text-red-600';
}

function CopyableSecret({ label, value, hint }: { label: string; value: string; hint: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="text-xs font-medium text-amber-900">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-surface px-2 py-1 font-mono text-xs text-slate-800">
          {value}
        </code>
        <button
          onClick={() => {
            navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="shrink-0 rounded border border-amber-300 bg-surface p-1.5 text-amber-700 hover:bg-amber-100"
          title="Kopieren"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <p className="mt-2 text-xs text-amber-800">{hint}</p>
    </div>
  );
}

export default function ActivationPanel({ instance }: { instance: Instance }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showStateForm, setShowStateForm] = useState(false);
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [error, setError] = useState('');
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);

  const [newState, setNewState] = useState<ActivationState>('PAYMENT_OVERDUE');
  const [reason, setReason] = useState('');
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeBody, setNoticeBody] = useState('');
  const [noticeSeverity, setNoticeSeverity] = useState<NoticeSeverity>('WARN');
  const [noticeAudience, setNoticeAudience] = useState<NoticeAudience>('ADMINS');

  const { data: notices = [] } = useQuery({
    queryKey: ['activation-notices', instance.id],
    queryFn: () => activation.listNotices(instance.id),
  });

  const { data: heartbeats = [] } = useQuery({
    queryKey: ['activation-heartbeats', instance.id],
    queryFn: () => activation.heartbeats(instance.id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['instance', instance.id] });
    qc.invalidateQueries({ queryKey: ['activation-notices', instance.id] });
  };

  const setStateMutation = useMutation({
    mutationFn: () => {
      const restricting = newState === 'SUSPENDED' || newState === 'TERMINATED';
      return activation.setState(instance.id, {
        state: newState,
        reason,
        // The API rejects a restriction without a notice. Sending it from here
        // keeps the rule in one place instead of duplicating the validation.
        notice: restricting
          ? {
              title: noticeTitle,
              body: noticeBody,
              audience: noticeAudience,
              dismissible: false,
            }
          : undefined,
      });
    },
    onSuccess: () => {
      setShowStateForm(false);
      setReason('');
      setNoticeTitle('');
      setNoticeBody('');
      setError('');
      invalidate();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message ?? 'Änderung fehlgeschlagen');
    },
  });

  const noticeMutation = useMutation({
    mutationFn: () =>
      activation.createNotice(instance.id, {
        severity: noticeSeverity,
        audience: noticeAudience,
        title: noticeTitle,
        body: noticeBody,
      }),
    onSuccess: () => {
      setShowNoticeForm(false);
      setNoticeTitle('');
      setNoticeBody('');
      setError('');
      invalidate();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message ?? 'Hinweis konnte nicht angelegt werden');
    },
  });

  const retireMutation = useMutation({
    mutationFn: (noticeId: string) => activation.retireNotice(noticeId),
    onSuccess: invalidate,
  });

  const keyMutation = useMutation({
    mutationFn: () => activation.issueKey(instance.id),
    onSuccess: (data) => {
      setIssuedKey(data.activationKey);
      invalidate();
    },
  });

  const tokenMutation = useMutation({
    mutationFn: () => activation.rotateToken(instance.id),
    onSuccess: (data) => {
      setIssuedToken(data.agentToken);
      invalidate();
    },
  });

  const { cls, Icon } = stateStyle(instance.activationState);
  const heartbeatHours = instance.lastHeartbeatAt
    ? Math.floor((Date.now() - new Date(instance.lastHeartbeatAt).getTime()) / 3_600_000)
    : null;
  const activeNotices = notices.filter(
    (n) => !n.activeUntil || new Date(n.activeUntil) > new Date(),
  );
  const restricting = newState === 'SUSPENDED' || newState === 'TERMINATED';

  return (
    <div className="space-y-6">
      {/* ── State ────────────────────────────────────────────────────────── */}
      <div className={`rounded-lg border p-4 ${cls}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Icon size={22} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">{STATE_LABEL[instance.activationState]}</div>
              <div className="text-sm opacity-90">
                Wirkung: {ENFORCEMENT_LABEL[
                  instance.activationState === 'ACTIVE'
                    ? 'NONE'
                    : instance.activationState === 'PAYMENT_OVERDUE'
                      ? 'NOTICE'
                      : instance.activationGraceStartedAt
                        ? levelFromGrace(instance.activationGraceStartedAt)
                        : 'NOTICE'
                ]}
              </div>
              {instance.activationReason && (
                <div className="mt-1 text-sm opacity-80">Grund: {instance.activationReason}</div>
              )}
              {instance.activationGraceStartedAt && (
                <div className="mt-1 text-xs opacity-75">
                  Seit {format(new Date(instance.activationGraceStartedAt), 'dd.MM.yyyy')} (Tag{' '}
                  {Math.floor(
                    (Date.now() - new Date(instance.activationGraceStartedAt).getTime()) /
                      86_400_000,
                  )}
                  ) — Stufen: Tag 0 Hinweis, Tag 14 nur Lesen, Tag 30 nur Admin + Export
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowStateForm((v) => !v)}
            className="shrink-0 rounded-lg border border-current bg-surface/70 px-3 py-1.5 text-sm font-medium hover:bg-surface"
          >
            Status ändern
          </button>
        </div>

        {showStateForm && (
          <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-surface p-4 text-slate-700">
            <div>
              <label className="block text-xs font-medium text-slate-600">{t('activation.newState')}</label>
              <select
                value={newState}
                onChange={(e) => setNewState(e.target.value as ActivationState)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="ACTIVE">{t('activation.stateActive')}</option>
                <option value="PAYMENT_OVERDUE">
                  Zahlung offen — nur Hinweis, keine Einschränkung
                </option>
                <option value="SUSPENDED">{t('activation.stateBlocked')}</option>
                <option value="TERMINATED">{t('activation.stateTerminated')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600">
                Grund (intern, landet im Audit-Log)
              </label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('activation.reasonExample')}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            {restricting && (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs text-amber-900">
                  Eine Einschränkung wird nie stillschweigend gesetzt — der Kunde muss sehen,
                  warum und was zu tun ist. Diese Nachricht erscheint in der App.
                </p>
                <input
                  value={noticeTitle}
                  onChange={(e) => setNoticeTitle(e.target.value)}
                  placeholder={t('activation.noticeTitleExample')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <textarea
                  value={noticeBody}
                  onChange={(e) => setNoticeBody(e.target.value)}
                  rows={4}
                  placeholder={t('activation.noticeBodyHint')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            )}

            {error && <div className="text-sm text-red-600">{error}</div>}

            <div className="flex gap-2">
              <button
                onClick={() => setStateMutation.mutate()}
                disabled={
                  setStateMutation.isPending ||
                  reason.trim().length < 3 ||
                  (restricting && (noticeTitle.trim().length < 3 || noticeBody.trim().length < 3))
                }
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-surface disabled:opacity-40"
              >
                {setStateMutation.isPending ? 'Speichern…' : 'Übernehmen'}
              </button>
              <button
                onClick={() => {
                  setShowStateForm(false);
                  setError('');
                }}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Call-home ────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-surface p-4">
        <div className="mb-3 flex items-center gap-2 font-medium text-slate-800">
          <Activity size={18} /> Rückmeldung der Instanz
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
          <div>
            <dt className="text-xs text-slate-500">{t('activation.lastContact')}</dt>
            <dd className={`font-medium ${heartbeatTone(heartbeatHours)}`}>
              {instance.lastHeartbeatAt
                ? `vor ${heartbeatHours} h`
                : 'noch nie'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t('activation.reportedVersion')}</dt>
            <dd className="font-medium text-slate-800">{instance.reportedVersion ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Agent</dt>
            <dd className="font-medium text-slate-800">{instance.agentVersion ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{t('activation.tokenValidUntil')}</dt>
            <dd className="font-medium text-slate-800">
              {instance.activationValidUntil
                ? format(new Date(instance.activationValidUntil), 'dd.MM.yyyy HH:mm')
                : '—'}
            </dd>
          </div>
        </dl>

        <p className="mt-3 text-xs text-slate-500">
          Erwartet wird ein Kontakt pro Tag. Bleibt er aus, läuft das Aktivierungs-Token nach
          7 Tagen ab — Entzug heißt hier „nicht mehr verlängern“, nicht „Abschaltbefehl senden“.
        </p>

        {heartbeats.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-medium text-slate-600">
              Verlauf ({heartbeats.length})
            </summary>
            <table className="mt-2 w-full text-xs">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1">Zeit</th>
                  <th className="py-1">Version</th>
                  <th className="py-1">{t('activation.stateAccordingToInstance')}</th>
                  <th className="py-1">Kennzahlen</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {heartbeats.slice(0, 20).map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="py-1">{format(new Date(h.receivedAt), 'dd.MM. HH:mm')}</td>
                    <td className="py-1">{h.version ?? '—'}</td>
                    <td className="py-1">{h.reportedActivationState ?? '—'}</td>
                    <td className="py-1">
                      {Object.entries(h.metrics ?? {})
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>

      {/* ── Notices ──────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium text-slate-800">
            <MessageSquarePlus size={18} /> Nachrichten in der App
          </div>
          <button
            onClick={() => setShowNoticeForm((v) => !v)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Nachricht anlegen
          </button>
        </div>

        {showNoticeForm && (
          <div className="mb-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">Dringlichkeit</label>
                <select
                  value={noticeSeverity}
                  onChange={(e) => setNoticeSeverity(e.target.value as NoticeSeverity)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                >
                  <option value="INFO">Info</option>
                  <option value="WARN">Warnung</option>
                  <option value="BLOCKING">
                    Sperrhinweis (nur bei bestehender Einschränkung)
                  </option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">{t('activation.recipients')}</label>
                <select
                  value={noticeAudience}
                  onChange={(e) => setNoticeAudience(e.target.value as NoticeAudience)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                >
                  <option value="ADMINS">{t('activation.adminsOnly')}</option>
                  <option value="ALL">{t('activation.allUsers')}</option>
                </select>
              </div>
            </div>
            <input
              value={noticeTitle}
              onChange={(e) => setNoticeTitle(e.target.value)}
              placeholder="Titel"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
              value={noticeBody}
              onChange={(e) => setNoticeBody(e.target.value)}
              rows={3}
              placeholder={t('activation.messageBody')}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              onClick={() => noticeMutation.mutate()}
              disabled={
                noticeMutation.isPending ||
                noticeTitle.trim().length < 3 ||
                noticeBody.trim().length < 3
              }
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-surface disabled:opacity-40"
            >
              Anlegen
            </button>
          </div>
        )}

        {activeNotices.length === 0 ? (
          <p className="text-sm text-slate-500">{t('activation.noActiveNotices')}</p>
        ) : (
          <ul className="space-y-2">
            {activeNotices.map((n) => (
              <li
                key={n.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                        n.severity === 'BLOCKING'
                          ? 'bg-red-100 text-red-700'
                          : n.severity === 'WARN'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {n.severity}
                    </span>
                    <span className="text-sm font-medium text-slate-800">{n.title}</span>
                    {n.automated && (
                      <span
                        className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
                        title={t('activation.systemGenerated')}
                      >
                        automatisch
                      </span>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{n.body}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {n.audience === 'ALL' ? 'Alle Nutzer' : 'Nur Admins'} · seit{' '}
                    {format(new Date(n.activeFrom), 'dd.MM.yyyy')}
                  </p>
                </div>
                <button
                  onClick={() => retireMutation.mutate(n.id)}
                  className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                  title={t('activation.withdrawNotice')}
                >
                  Zurückziehen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Keys ─────────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-surface p-4">
        <div className="mb-3 flex items-center gap-2 font-medium text-slate-800">
          <KeyRound size={18} /> Zugangsdaten der Instanz
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500">{t('activation.activationKey')}</dt>
            <dd className="font-medium text-slate-800">
              {instance.activationKeyUsedAt
                ? `eingelöst am ${format(new Date(instance.activationKeyUsedAt), 'dd.MM.yyyy')}`
                : 'noch nicht eingelöst'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Agent-Token</dt>
            <dd className="font-mono text-xs font-medium text-slate-800">
              {instance.agentTokenPrefix ? `${instance.agentTokenPrefix}…` : '—'}
            </dd>
          </div>
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => keyMutation.mutate()}
            disabled={keyMutation.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <KeyRound size={14} />
            {instance.activationKeyUsedAt
              ? 'Neuen Aktivierungsschlüssel ausgeben'
              : 'Aktivierungsschlüssel ausgeben'}
          </button>
          <button
            onClick={() => tokenMutation.mutate()}
            disabled={tokenMutation.isPending || !instance.agentTokenPrefix}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw size={14} /> Agent-Token rotieren
          </button>
        </div>

        {issuedKey && (
          <CopyableSecret
            label="Aktivierungsschlüssel — wird nur einmal angezeigt"
            value={issuedKey}
            hint="Der Kunde trägt ihn als JUNI_ACTIVATION_KEY in seine .env ein. Beim ersten Start registriert sich der Stack damit und erhält sein Agent-Token. Eine Neuausgabe entwertet die bisherige Installation."
          />
        )}
        {issuedToken && (
          <CopyableSecret
            label="Agent-Token — wird nur einmal angezeigt"
            value={issuedToken}
            hint="Nur nötig, wenn die Instanz sich nicht neu registrieren kann. Das bisherige Token ist ab jetzt ungültig."
          />
        )}
      </div>
    </div>
  );
}

/** Same thresholds as the backend ladder — kept in sync deliberately, not imported. */
function levelFromGrace(graceStartedAt: string): EnforcementLevel {
  const days = Math.floor((Date.now() - new Date(graceStartedAt).getTime()) / 86_400_000);
  if (days >= 30) return 'ADMIN_ONLY';
  if (days >= 14) return 'READ_ONLY';
  return 'NOTICE';
}
